<template lang="pug">
modal#sign-envelope-modal(
  :close-button-label='$t("button.cancel")'
  :disable-process-button='!canSubmit'
  :process-button-label='$t("modal.signEnvelope.sign")'
  :title='$t("modal.signEnvelope.title")'
  @process='sign'
  aria-hidden='true'
  ref='modal'
  size='sm'
)
  .form-group
    label {{ $t('modal.signEnvelope.cpfLabel') }}
    .form-control-icon
      icon.icon-color(name='id-card')
      input#exampleInputRole.form-control(
        :class='{ "is-invalid": isCpfInvalid }'
        :placeholder='$t("modal.signEnvelope.cpfPlaceholder")'
        @blur='cpfTouched = true'
        @input='errorMsg = ""; onCpfInput($event)'
        type='text'
        v-model='cpf_cnpj'
      )
    .invalid-feedback(v-if='isCpfInvalid')
      | {{ $t('modal.signEnvelope.cpfError') }}

  .form-group
    label {{ $t('modal.signEnvelope.otpLabel') }}
    .form-control-icon
      icon.icon-color(name='user')
      input#exampleInputRole.form-control(
        :class='{ "is-invalid": isInvalidOtp }'
        :placeholder='$t("modal.signEnvelope.otpPlaceholder")'
        @blur='otpTouched = true'
        @input='errorMsg = ""'
        type='text'
        v-model='otp'
      )
    .invalid-feedback(v-if='isInvalidOtp')
      | {{ $t('validations.otp') }}

  .alert.alert-warning(role='alert' v-if='errorMsg')
    | {{ errorMsg }}
</template>

<script>
import Modal from '@/ui/molecules/Modal.vue';
import Icon from '@/ui/atoms/Icon.vue';
import BirdIdService from '@/modules/business/services/http/envelopes/BirdIdService';
import rules from '@/utils/ValidateRules';

export default {
  name: 'SignEnvelopeWithoutSignatureModal',
  components: {
    Modal,
    Icon,
  },
  data() {
    return {
      cpf_cnpj: '',
      errorMsg: '',
      otp: '',
      cpfTouched: false,
      otpTouched: false,
    };
  },
  computed: {
    isCpfValid() {
      return rules.cpf.validate(this.cpf_cnpj);
    },
    isCpfInvalid() {
      return this.cpfTouched && !this.isCpfValid;
    },
    isValidOtp() {
      return rules.otp.validate(this.otp);
    },
    isInvalidOtp() {
      return this.otpTouched && !this.isValidOtp;
    },
    canSubmit() {
      return this.isCpfValid && this.isValidOtp;
    },
  },
  methods: {
    onCpfInput(event) {
      const rawCpf = event.target.value.replace(/\D/g, '').slice(0, 11);
      const cpfWithMask = this.formatCpf(rawCpf);
      this.cpf_cnpj = cpfWithMask;
    },
    formatCpf(digitos) {
      return String(digitos)
        .replace(/^(\d{3})(\d)/, '$1.$2')
        .replace(/^(\d{3}\.\d{3})(\d)/, '$1.$2')
        .replace(/^(\d{3}\.\d{3}\.\d{3})(\d)/, '$1-$2');
    },
    selectLatestValid(validCerts) {
      if (validCerts.length === 0) {
        return null;
      }
      return validCerts.reduce((latest, cert) => {
        if (!latest) {
          return cert;
        }
        const certEnd = new Date(cert.validity.not_after);
        const latestEnd = new Date(latest.validity.not_after);
        return certEnd > latestEnd ? cert : latest;
      }, null);
    },
    isWithinValidity(cert) {
      const now = new Date();
      const start = new Date(cert.validity.not_before);
      const end = new Date(cert.validity.not_after);
      return now >= start && now <= end;
    },
    chooseLatestValid(certArray) {
      return certArray.reduce((latest, cert) => {
        if (!latest) return cert;
        const certEnd = new Date(cert.validity.not_after);
        const latestEnd = new Date(latest.validity.not_after);
        return certEnd > latestEnd ? cert : latest;
      }, null);
    },
    categorizeCertificates(certs) {
      const revokedCerts = [];
      const validCerts = [];
      const expiredCerts = [];

      certs.forEach((cert) => {
        if (cert.was_revoked) {
          revokedCerts.push(cert);
          return;
        }
        if (cert.has_valid_date) {
          validCerts.push(cert);
          return;
        }
        expiredCerts.push(cert);
      });

      return { validCerts, revokedCerts, expiredCerts };
    },
    getErrorMessage({ validCerts, revokedCerts, expiredCerts }) {
      if (validCerts.length > 0) return null;
      if (revokedCerts.length > 0)
        return this.$t('modal.signEnvelope.error.revokedCert');
      if (expiredCerts.length > 0)
        return this.$t('modal.signEnvelope.error.expiredCerts');
      return this.$t('modal.signEnvelope.error.notFindCert');
    },
    buildErrorMessage(message) {
      const PREFIX = this.$t('modal.signEnvelope.error.cannotSign');
      return `${PREFIX} ${message}`;
    },
    async sign() {
      this.$emit('initiateSignatureProcess'); // init loading in parent
      try {
        const rawCpf = rules.stripNonDigits(this.cpf_cnpj);

        const response = await BirdIdService.getToken({
          cpf_cnpj: rawCpf,
          otp: this.otp,
        });

        const certs = response.data.certificates;

        if (!Array.isArray(certs) || certs.length === 0) {
          this.errorMsg = this.buildErrorMessage(
            this.$t('modal.signEnvelope.error.notFindCert')
          );
          return;
        }

        const { validCerts, revokedCerts, expiredCerts } =
          this.categorizeCertificates(certs);

        const chosen = this.selectLatestValid(validCerts);
        if (chosen) {
          this.$emit('acquiredToken', {
            access_token: response.data.access_token,
            certificate_alias: chosen.alias,
          });
          this.$refs.modal.close();
          return;
        }

        const baseError = this.getErrorMessage({
          validCerts,
          revokedCerts,
          expiredCerts,
        });
        this.errorMsg = this.buildErrorMessage(baseError);
      } catch (error) {
        console.error({
          message: 'Error during Bird ID token acquisition',
          error: error,
          code: error?.code,
          timestamp: new Date().toISOString(),
        });
        this.$emit('finalizeLoadingProcess');
        if (error.isInvalidCredentials) {
          this.errorMsg = this.buildErrorMessage(error.message);
          return;
        }
        this.errorMsg = `${this.$t(
          'modal.signEnvelope.error.birdIdAuth'
        )} ${this.$t('modal.signEnvelope.error.tryAgain')}`;
        return;
      }
    },
  },
};
</script>

<style scoped>
.form-control-icon {
  position: relative;
}

.form-group label {
  color: #202020;
  font-size: 16px;
  font-weight: 400;
}

.form-control-icon .input-icon {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  pointer-events: none;
  color: #6c757d;
  width: 1rem;
  height: 1rem;
}

.form-control-icon input {
  padding-left: 2.5rem;
  border-radius: 15px;
  font-size: 16px;
}

.is-invalid {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.invalid-feedback {
  display: block;
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.icon-color {
  color: #135650;
}
</style>
